/**
 * AppErrorBoundary - Sprint 1, F1.6
 * ==================================
 *
 * Top-level error boundary wrapping the entire SPA so unhandled render
 * errors show a branded fallback instead of a white screen. Captured
 * errors are reported (best-effort) to `/api/system/client-errors` so we
 * can monitor regressions in production.
 *
 * Usage (in App.js):
 *   <AppErrorBoundary>
 *     <BrowserRouter> ... </BrowserRouter>
 *   </AppErrorBoundary>
 */
import React from 'react';
import ErrorMonitor from '../../services/errorMonitoring';

class AppErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      hasError: false,
      errorId: null,
      message: null,
    };
  }

  static getDerivedStateFromError(error) {
    // iter1114b - Stale-bundle chunk failures should self-heal by reloading
    // the page, not by showing the crash screen. The reload picks up the
    // fresh index.html + new chunk hashes.
    //
    // iter1238 Phase 8.13c - the previous `window.location.reload()` used
    // the browser cache and could refetch the SAME stale bundle in a loop,
    // producing another chunk-fail crash within 15 s → fell through to the
    // crash card. Fix: hard cache-bust via a fresh URL + clear cached
    // service-worker / cache-API entries before navigating.
    try {
      const msg = String(error && (error.message || error));
      const isChunkFail = /Loading chunk|ChunkLoadError|Failed to fetch dynamically imported|Importing a module script failed/i.test(msg);
      if (isChunkFail && typeof window !== 'undefined') {
        const key = 'akf_chunk_reload_at';
        const now = Date.now();
        const last = parseInt(window.sessionStorage.getItem(key) || '0', 10);
        if (!last || now - last > 15000) {
          window.sessionStorage.setItem(key, String(now));
          // Best-effort: nuke Cache API + service-worker registrations so
          // the reload actually fetches new bytes from the CDN.
          try {
            if ('caches' in window) {
              window.caches.keys().then((names) => {
                names.forEach((n) => window.caches.delete(n));
              }).catch(() => {});
            }
            if ('serviceWorker' in navigator) {
              navigator.serviceWorker.getRegistrations().then((regs) => {
                regs.forEach((r) => { try { r.unregister(); } catch (_) { /* noop */ } });
              }).catch(() => {});
            }
          } catch (_swKill) { /* non-blocking */ }
          // Hard reload with a cache-buster so the SW/CDN can't serve the
          // stale index.html again.
          const url = new URL(window.location.href);
          url.searchParams.set('_akf_bust', String(now));
          window.location.replace(url.toString());
          // Suppress the crash UI while the reload happens.
          return { hasError: false };
        }
      }
    } catch (_swallow) { /* fall through - render the crash card */ }
    return {
      hasError: true,
      errorId: `EBND-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 6)}`,
      message: error?.message || 'Unknown error',
    };
  }

  componentDidCatch(error, errorInfo) {
    // Best-effort report - don't crash the boundary if reporting fails.
    try {
      ErrorMonitor.capture(error, { type: 'react_render', componentStack: errorInfo?.componentStack?.slice(0, 1500), errorId: this.state.errorId });
    } catch (_e) { /* noop */ }
    try {
      // eslint-disable-next-line no-console
      console.error('[AppErrorBoundary]', this.state.errorId, error, errorInfo);
      const apiBase = (typeof window !== 'undefined'
        && window.location.hostname !== 'localhost'
        && window.location.hostname !== '127.0.0.1')
        ? '/api'
        : (process.env.REACT_APP_BACKEND_URL || '') + '/api';
      const payload = {
        error_id: this.state.errorId,
        message: this.state.message,
        component_stack: errorInfo?.componentStack || null,
        url: typeof window !== 'undefined' ? window.location.href : null,
        user_agent: typeof navigator !== 'undefined' ? navigator.userAgent : null,
        timestamp: new Date().toISOString(),
      };
      // Fire and forget; do not block the UI.
      fetch(`${apiBase}/system/client-errors`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
        keepalive: true,
      }).catch(() => {});
    } catch (_e) { /* swallow */ }
  }

  handleReload = () => {
    // iter1238 Phase 8.13c · same hard-reload dance as the auto-recovery
    // above so users clicking "Reload page" don't loop back into the
    // same stale bundle.
    if (typeof window === 'undefined') return;
    try {
      if ('caches' in window) {
        window.caches.keys().then((names) => {
          names.forEach((n) => window.caches.delete(n));
        }).catch(() => {});
      }
      if ('serviceWorker' in navigator) {
        navigator.serviceWorker.getRegistrations().then((regs) => {
          regs.forEach((r) => { try { r.unregister(); } catch (_) { /* noop */ } });
        }).catch(() => {});
      }
    } catch (_swKill) { /* non-blocking */ }
    const url = new URL(window.location.href);
    url.searchParams.set('_akf_bust', String(Date.now()));
    window.location.replace(url.toString());
  };

  handleGoHome = () => {
    if (typeof window !== 'undefined') window.location.assign('/');
  };

  handleReport = () => {
    try {
      window.dispatchEvent(new CustomEvent('akf:report-issue', {
        detail: {
          issueContext: {
            trigger: 'app_crash',
            error_id: this.state.errorId,
            message: this.state.message,
            url: typeof window !== 'undefined' ? window.location.href : null,
          },
          fingerprint: `app_crash:${typeof window !== 'undefined' ? window.location.pathname : ''}`,
        },
      }));
    } catch (_e) { /* noop */ }
  };

  render() {
    if (!this.state.hasError) return this.props.children;

    return (
      <div
        className="min-h-dvh w-full flex items-center justify-center px-6 py-12 bg-background"
        data-testid="app-error-boundary"
      >
        <div className="max-w-md w-full bg-card border border-border rounded-2xl shadow-lg p-8 text-center">
          <div className="w-16 h-16 rounded-2xl mx-auto mb-6 flex items-center justify-center bg-gradient-to-br from-[#283F8B] to-[#108981]">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 24 24"
              fill="none"
              stroke="white"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              className="w-8 h-8"
              aria-hidden="true"
            >
              <path d="M12 9v4" />
              <path d="M12 17h.01" />
              <path d="m10.29 3.86-8.18 14.2A2 2 0 0 0 3.83 21h16.34a2 2 0 0 0 1.72-2.94l-8.18-14.2a2 2 0 0 0-3.42 0z" />
            </svg>
          </div>
          <h1 className="text-2xl font-bold text-foreground mb-2">
            Something went wrong
          </h1>
          <p className="text-sm text-muted-foreground mb-6">
            We hit an unexpected error. Your data is safe: we've logged this and
            will look into it.
          </p>

          <div className="bg-muted/50 rounded-lg px-3 py-2 mb-6">
            <p className="text-xs text-muted-foreground">Reference</p>
            <p
              className="font-mono text-xs font-semibold text-foreground select-all"
              data-testid="app-error-reference"
            >
              {this.state.errorId}
            </p>
          </div>

          <div className="flex flex-col gap-2">
            <button
              type="button"
              onClick={this.handleReload}
              data-testid="app-error-reload"
              className="w-full px-4 py-2.5 rounded-xl bg-[#108981] text-white font-medium hover:bg-[#0a6b65] transition-colors"
            >
              Reload the page
            </button>
            <button
              type="button"
              onClick={this.handleReport}
              data-testid="app-error-report-issue"
              className="w-full px-4 py-2.5 rounded-xl border border-rose-200 text-rose-600 font-medium hover:bg-rose-50 transition-colors"
            >
              Report this issue
            </button>
            <button
              type="button"
              onClick={this.handleGoHome}
              data-testid="app-error-home"
              className="w-full px-4 py-2.5 rounded-xl border border-border text-foreground hover:bg-muted transition-colors"
            >
              Go to home
            </button>
            <a
              href={`mailto:team@akountfly.com?subject=Error%20${encodeURIComponent(this.state.errorId || '')}&body=I%20hit%20an%20error.%20Reference%3A%20${encodeURIComponent(this.state.errorId || '')}`}
              data-testid="app-error-email-support"
              className="text-xs text-muted-foreground hover:text-foreground underline mt-2"
            >
              Email support
            </a>
          </div>
        </div>
      </div>
    );
  }
}

export default AppErrorBoundary;
